import type * as Tensorflow from '@tensorflow/tfjs';
import { decryptPayload } from 'obfuscated/inquiry/lib/utils/crypto';

/**
 * Since the model we import is encrypted, we need a custom IOHandler in order
 * to decrypt both the manifest and the binaries for the model weights.
 */
export class CustomModelIOHandler implements Tensorflow.io.IOHandler {
  public tf: typeof Tensorflow;
  public modelJson: Tensorflow.io.ModelJSON;
  public modelPath: string;

  constructor(tf: typeof Tensorflow, modelJson: Tensorflow.io.ModelJSON, modelPath: string) {
    this.tf = tf;
    this.modelJson = modelJson;
    this.modelPath = modelPath;
  }

  async load() {
    const modelJSON = this.modelJson;
    return this.tf.io.getModelArtifactsForJSON(modelJSON, (weightsManifest) =>
      this.loadWeights(weightsManifest),
    );
  }

  private async loadWeights(
    weightsManifest: Tensorflow.io.WeightsManifestConfig,
  ): Promise<[Tensorflow.io.WeightsManifestEntry[], ArrayBuffer]> {
    const fetchURLs = await this.getWeightUrls(weightsManifest);
    const weightSpecs = this.tf.io.getWeightSpecs(weightsManifest);
    const buffers = await this.loadWeightsAsArrayBuffer(fetchURLs);
    return [weightSpecs, buffers];
  }

  private async loadWeightsAsArrayBuffer(fetchURLs: string[]): Promise<ArrayBuffer> {
    const requests = fetchURLs.map((fetchURL) => window.fetch(fetchURL));
    const responses = await Promise.all(requests);
    const encryptedPromises = responses.map((response) => response.text());
    const encryptedBuffers = await Promise.all(encryptedPromises);
    const decryptedBuffers = await Promise.all(
      encryptedBuffers.map((buffer) => decryptPayload(buffer, false)),
    );

    return mergeArrayBuffers(decryptedBuffers);
  }

  private async getWeightUrls(
    weightsManifest: Tensorflow.io.WeightsManifestConfig,
  ): Promise<string[]> {
    const fetchURLs: string[] = [];
    for (const weightsGroup of weightsManifest) {
      for (const path of weightsGroup.paths) {
        fetchURLs.push(this.modelPath + path);
      }
    }
    return fetchURLs;
  }
}

function mergeArrayBuffers(buffers: ArrayBuffer[]): ArrayBuffer {
  const totalLength = buffers.reduce((sum, buffer) => sum + buffer.byteLength, 0);
  const mergedBuffer = new Uint8Array(totalLength);
  let offset = 0;
  buffers.forEach((buffer) => {
    mergedBuffer.set(new Uint8Array(buffer), offset);
    offset += buffer.byteLength;
  });

  return mergedBuffer.buffer;
}
